'use client'
import { MenuIcon, XMarkIcon } from '@styleguide/components/ui/icons'
import { useState } from 'react'
import { Sheet, SheetContent } from '@styleguide/components/ui/sheet'
import Caption from '@shared/typography/Caption'
import Link from 'next/link'
import { FaExternalLinkAlt } from 'react-icons/fa'
import { usePathname } from 'next/navigation'
import H3 from '@shared/typography/H3'
import DashboardMobile from '../DashboardMobile'
import {
  ABOUT_US_LINKS,
  RESOURCES_LINKS,
  PRODUCT_LINKS,
} from '@shared/layouts/navigation/NavigationProvider'
import { useUser } from '@shared/hooks/useUser'
import { useCampaignStatus } from '@shared/hooks/useCampaignStatus'
import { Button } from '@styleguide'
import { User } from 'helpers/types'

interface NavLink {
  href: string
  id: string
  label: string
  icon: React.ReactNode
  external?: boolean
}

interface NavSection {
  title: string
  links: NavLink[]
}

const sections: NavSection[] = [
  { title: 'Product', links: PRODUCT_LINKS as NavLink[] },
  { title: 'Resources', links: RESOURCES_LINKS as NavLink[] },
  { title: 'About Us', links: ABOUT_US_LINKS as NavLink[] },
]

const RightSideMobile = (): React.JSX.Element => {
  const [isOpen, setOpen] = useState(false)
  const [user] = useUser()
  const [campaignStatus] = useCampaignStatus()
  const { status, slug, step } =
    (campaignStatus as {
      status?: boolean | string
      slug?: string
      step?: string | number
    }) || {}
  const pathname = usePathname()
  const isDashboardPath = pathname?.startsWith('/dashboard')
  const isOnboardingPath = pathname?.startsWith('/onboarding')
  const dashboardLink = '/dashboard'

  const closeMenu = () => {
    setOpen(false)
  }

  if (isOnboardingPath) {
    return <></>
  }

  return (
    <div className="lg:hidden">
      <div>
        <div
          className={`z-[1300] fixed top-1 right-0 flex items-center ${
            isOpen ? 'text-white' : ''
          }`}
        >
          <button
            type="button"
            onClick={() => setOpen(!isOpen)}
            aria-label={isOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isOpen}
            className="flex h-12 w-12 items-center justify-center"
          >
            {isOpen ? <XMarkIcon size={28} /> : <MenuIcon size={28} />}
          </button>
        </div>
        <Sheet open={isOpen} onOpenChange={setOpen}>
          <SheetContent
            side="right"
            className="p-0 border-0 w-auto max-w-none sm:max-w-none [&>button:last-child]:hidden"
          >
            {user && isDashboardPath ? (
              <DashboardMobile user={user as User} pathname={pathname || ''} />
            ) : (
              <div className="flex flex-col w-[270px] bg-secondary text-white h-screen relative">
                <div
                  className={`grow overflow-auto px-4 pt-24 ${
                    user ? 'pb-36' : 'pb-60'
                  }`}
                >
                  {user && (
                    <H3 className="mb-8">
                      {(user as User).firstName} {(user as User).lastName}
                    </H3>
                  )}
                  {sections.map((section) => (
                    <div
                      key={section.title}
                      className="border-b border-indigo-400 pb-3 mb-3"
                    >
                      <Caption className="py-2">{section.title}</Caption>
                      {section.links.map((link) => (
                        <Link
                          href={link.href}
                          id={`mobile-nav-${link.id}`}
                          key={link.id}
                          className="block no-underline font-medium py-3 whitespace-nowrap text-base px-2 rounded flex items-center justify-between"
                          target={link.external ? '_blank' : undefined}
                          rel={
                            link.external ? 'noopener noreferrer nofollow' : ''
                          }
                          onClick={closeMenu}
                        >
                          <div className="flex items-center">
                            {link.icon}
                            <div className="ml-3">{link.label}</div>
                          </div>
                          {link.external && <FaExternalLinkAlt size={14} />}
                        </Link>
                      ))}
                    </div>
                  ))}
                </div>
                <div className="w-full h-auto sticky bottom-0">
                  <div className="p-6 bg-secondary h-auto">
                    {user ? (
                      <>
                        {status === 'candidate' && !isDashboardPath && (
                          <Button
                            asChild
                            variant="neutral"
                            size="large"
                            id="mobile-nav-dashboard"
                            onClick={closeMenu}
                            className="w-full font-medium focus-visible:outline-white/40"
                          >
                            <Link href={dashboardLink}>Campaign Manager</Link>
                          </Button>
                        )}
                        {status === 'onboarding' && (
                          <Button
                            asChild
                            variant="neutral"
                            size="large"
                            id="mobile-nav-continue-onboarding"
                            onClick={closeMenu}
                            className="w-full font-medium focus-visible:outline-white/40"
                          >
                            <Link href={`/onboarding/${slug}/${step || 1}`}>
                              Continue Onboarding
                            </Link>
                          </Button>
                        )}
                      </>
                    ) : (
                      <>
                        <Link
                          href="/login"
                          onClick={closeMenu}
                          className="block w-full text-white py-2 mb-2 text-center font-medium"
                        >
                          Log In
                        </Link>
                        <Button
                          asChild
                          size="large"
                          id="mobile-nav-sign-up"
                          onClick={closeMenu}
                          className="w-full text-white text-center font-medium focus-visible:outline-white/40"
                        >
                          <Link href="/sign-up">Sign Up</Link>
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}
          </SheetContent>
        </Sheet>
      </div>
    </div>
  )
}

export default RightSideMobile
